import React, { useState, useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Bell
  } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserProfile from './Land/UserProfile';
import QuickAccessModal from './Land/QuickAccessModal';
import EnhancedSecDashboard from './Land/EnhancedSecDashboard';
import EnhancedCarousel from './Land/EnhancedCarousel';
import Footer from './Land/Footer';
import axios from 'axios'; 
import Avatar from '../../services/Avatar';
import layerFFLogo from '../../assets/images/layerff-icon.png'; 
import SEOHelmet from '../SEOHelmet';

const handleLogout = async () => {
  try {
    const response = await fetch('/d-auth/logout', {
      method: 'POST', // or 'GET', depending on your backend implementation
      credentials: 'include', // include cookies with the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {       
      
      window.location.href = '/'; 
    } else {
      // If there's an error during logout
      const errorData = await response.json();
      //console.error('Logout failed', errorData);
      alert( errorData ||'Logout failed. Please try again.');
    }
  } catch (error) {
    //console.error('Error during logout:', error);
    alert('An error occurred. Please try again.');
  }
};

const Landing = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isQuickAccessOpen, setIsQuickAccessOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
        try {
        const response = await axios.get('/users/me', { withCredentials: true });
        setCurrentUser(response.data); // Set user data
        } catch (error) {
        //console.error('Error fetching user profile:', error);
        } 
    };

    fetchUserProfile();
    }, []);

  // Enhanced Carousel Items with Background Images
  const carouselItems = [
    {
        title: "0x10LayerFF Platform",
        description: "Welcome to 0x10LayerFF - your multi-layered cybersecurity fortress. Our platform integrates 10 critical security layers (0x10 in hexadecimal) to form an impenetrable defense framework.",
        backgroundImage: "https://images.unsplash.com/photo-1563770660941-20978e870e26?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
        features: [
            "Unified security dashboard",
            "Cross-layer threat correlation",
            "Automated policy enforcement",
            "Real-time compliance monitoring"
        ],
        technologies: [
            "Hexagonal architecture",
            "AI-driven analytics",
            "Microservices design",
            "Zero-trust framework"
        ],
        icon: "🔐"
    },
    {
        title: "Secure Deletion",
        description: "Military-grade data destruction that goes beyond standard deletion. Our secure deletion tool ensures sensitive files are completely irrecoverable.",
        backgroundImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
        features: [
            "DoD 5220.22-M compliant",
            "Forensic-level erasure",
            "Multi-pass overwrite",
            "Drive sanitization reports"
        ],
        technologies: [
            "NIST SP 800-88",
            "Gutmann method",
            "Secure erase API",
            "Hardware-level wiping"
        ],
        icon: "🧹"
    },
     {
      title: "MetaFlyse",
      description: "MetaFlyse is a powerful tool for extracting, editing, adding, or deleting metadata in files. It can find hidden files and metadata, providing full control over file information.",
      backgroundImage: "https://canto-wp-media.s3.amazonaws.com/app/uploads/2020/04/19185944/metadata-1.jpg",
      features: [
          "Extract metadata from files",
          "Find hidden files and data",
          "Edit, add, or delete metadata",
          "Supports multiple file formats"
      ],
      technologies: [
          "Metadata extraction algorithms",
          "File system analysis",
          "Metadata editing APIs",
          "Hidden file detection"
      ],
      icon: "🛠️"
    },
     {
      title: "TriSec GraphAuth",
      description: "TriSec GraphAuth enables secure authentication using a three-level image graph system. It provides an innovative, layered approach to verify user identity with multiple image-based challenges.",
      backgroundImage: "https://cdn.slidesharecdn.com/ss_thumbnails/encencodedkl9j8pqazju-le8mxocfirt6bfyzlyohblh3b0ki1cckpxac0xp9dfsjpl-l2w-240924145041-4e702bc7-thumbnail.jpg?width=560&fit=bounds",
      features: [
          "Three-layer image verification",
          "Resistant to phishing and spoofing",
          "User-friendly visual authentication",
          "Enhanced security with layered checks"
      ],
      technologies: [
          "Image graph algorithms",
          "Layered authentication protocols",
          "Visual challenge generation",
          "Secure image storage"
      ],
      icon: "🔒"
    },
    {
        title: "ThreatEye",
        description: "Advanced vulnerability scanning tool that identifies, evaluates, and helps mitigate security risks in web applications and infrastructure.",
        backgroundImage: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS0vRSv5dYStRRH_SJnkqqxNKiaflODLcsLQg&s", 
        features: [
            "Comprehensive vulnerability assessment",
            "Real-time risk reporting",
            "Integration with CI/CD pipelines",
            "Detailed remediation guidance"
        ],
        technologies: [           
            "packet inspection",
            "Behavioral analysis",
            "Continuous monitoring"
        ],
        icon: "🦠" 
    },
    {
        title: "SubnetIQ",
        description: "Intelligent IPv4 & IPv6 subnet calculation and visualization tool with topology mapping, graphs, and validation.",
        backgroundImage: "https://eu-images.contentstack.com/v3/assets/bltde8121fc52c5c8f3/blt8b330216ba9b7816/661e498fa8c8f55d6f140878/IP5-F2NE1G.jpg", 
        features: [
          "IPv4 & IPv6 subnet calculation",
          "Network topology visualization",
          "Interactive network graphs",
          "Subnet and IP address validation",
          "VLAN configuration support"
        ],
        technologies: [
          "Subnetting algorithms (IPv4 & IPv6)",
          "Network topology discovery protocols",
          "Graph data structures and rendering",
          "IP address validation logic",
          "VLAN tagging and management protocols"
        ],
        icon: "🌐" 
    },
    {
        title: "Mobile/Email Analysis",
        description: "Advanced threat detection for mobile devices and email communications.",
        backgroundImage: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
       
        icon: "📱"
    },
    {
        title: "Phishing Analysis",
        description: "Real-time phishing detection with 99.8% accuracy. Our AI-powered system analyzes URLs and website structures.",
        backgroundImage: "https://images.unsplash.com/photo-1562813733-b31f71025d54?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
        features: [
            "Brand impersonation detection",
            "Homoglyph analysis",
            "Takedown automation",
            "User reporting portal"
        ],
        technologies: [           
            "BERT models",
            "WHOIS/DNS analysis",           
        ],
        icon: "🎣"
    },
    {
        title: "Cryptography & Hashing",
        description: "Enterprise-grade cryptographic tools with quantum-resistant algorithms. Secure your data with advanced encryption.",
        backgroundImage: "https://images.unsplash.com/photo-1639762681057-408e52192e55?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2232&q=80",
        features: [            
            "Key lifecycle management",
            "Cryptographic audit trails"
        ],
        technologies: [
            "AES-256/GCM",           
            "BLAKE3/SHA-3",
            "PKCS#11"
        ],
        icon: "🔑"
    },
    {
        title: "EduConnect Security",
        description: "Specialized protection for educational institutions. Safeguard student data and secure learning environments.",
        backgroundImage: "https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHwwfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80",
        features: [
            "FERPA compliance",
            "Exam proctoring tools",
            "LMS integrations",
            "Parental controls"
        ],
        technologies: [
            "LTI 1.3 integration",
            "Biometric verification",
            "Plagiarism detection",
            "Secure browser lockdown"
        ],
        icon: "🎓"
    },
    {
        title: "WAF & Network Defense",
        description: "Next-gen Web Application Firewall with machine learning capabilities. Block OWASP Top 10 threats in real-time.",
        backgroundImage: "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEgxW-ZK1AaS-X0odXwPtB4M-ed_J3qwhZq5s2ZPnJ0aebd3KHhS_JM0r-AMe-ALBebVOcyB7mFNaJXArGlvddWiQkhAveBkgwRkG_QTV1syCSc_BwZtL_lHk8eBktF3F-5CBR1e5H0M3dY/s0/waf.jpg",
        features: [
            "Behavioral analysis",
            "API protection",
            "Bot mitigation",
            "Geo-blocking"
        ],
        technologies: [
            "ModSecurity rules",
            "Machine learning models",
            "IP reputation feeds",
            "TLS inspection"
        ],
        icon: "🛡️"
    },
    {
        title: "Backup & Recovery",
        description: "Zero-trust encrypted backups with geo-redundancy. Immutable backups with ransomware protection.",
        backgroundImage: "https://bitperfection.com/wp-content/uploads/2020/09/Data-Backup.png",
        
        technologies: [
            "AES-256 at rest",
           
        ],
        icon: "💾"
    },
    {
        title: "SSO & Identity Fabric",
        description: "Secure Single Sign-On with behavioral biometrics. Our identity fabric integrates MFA and continuous authentication.",
        backgroundImage: "https://cdn.prod.website-files.com/61845f7929f5aa517ebab941/66d6c14aea60d1dc6eb965cb_Multi-Factor%20Authentication-%20How%20It%20Works%20and%20Why%20It%20Matters.jpg",
        features: [
            "Step-up authentication",
            "Device fingerprinting",           
            "Session risk scoring"
        ],       
        icon: "👤"
    },
    {
        title: "DevSecOps Automation",
        description: "Shift-left security for agile teams. Integrate security tools into CI/CD pipelines.",
        backgroundImage: "https://www.tmap.net/sites/tmap/files/styles/medium/public/images/260d.png?itok=-M4jgTmc",
        features: [
            "Pipeline security gates",
            "Infrastructure as Code scanning",
            "Secrets detection",
            "Dependency analysis"
        ],
        technologies: [
            "Semgrep/Checkov",
            "GitHub Actions",
            "Jenkins plugins",
            "SBOM generation"
        ],
        icon: "🛠️"
    },
    {
        title: "Threat Intelligence",
        description: "Proactive threat hunting with curated feeds from dark web and CVE databases.",
        backgroundImage: "https://www.csm.tech/storage/uploads/news/6707ad59b4af41728556377Thumb.jpg",
        features: [
            "APT tracking",
            "Zero-day alerts",
            "IOC management",
            "Threat actor profiling"
        ],
        technologies: [
            "STIX/TAXII",
            "MITRE ATT&CK",
            "Malware analysis",
            "Dark web monitoring"
        ],
        icon: "🔍"
    }
];

  
  return (
    <>
      <SEOHelmet page="Landing" />
    <div className={`
      min-h-screen flex flex-col transition-colors duration-300
      ${isDarkMode ? 'dark bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}
    `}>
      {/* Enhanced Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md">
        <div className="container mx-auto flex justify-between items-center p-4">
        <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                  <img src={layerFFLogo} alt="Logo" className="w-10  rounded-full h-15" />
                  <h1 className="text-2xl font-bold dark:text-white">0x10LayerFF</h1>
              </Link>
          </div>
            
            <div className="flex items-center space-x-4">
            <button 
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
                {isDarkMode ? '☀️' : '🌙'}
            </button>
            <button 
                onClick={() => setIsQuickAccessOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
            >
                Quick Access
            </button>
            <button 
                onClick={() => setIsProfileOpen(true)}
                className="flex items-center space-x-2 hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-full"
            >
            {currentUser ? (
                      <>
                        <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                <span className="text-xs text-gray-400">{currentUser.role}</span>
                            </div>
                      </>
                    ) : (
                        <span className="text-sm font-medium">Loading...</span>
                    )}
         
            </button>
            <button
                    onClick={() => setIsNotificationsOpen(true)} 
                    className="flex items-center space-x-2 hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-full relative"
                >
                    <Bell className="w-6 h-6 text-gray-600" />
                 
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-600 rounded-full"></span>
                </button>
                <button
                    onClick={handleLogout} 
                    className="flex items-center space-x-2 bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 p-2 rounded-full transition duration-200"
                >
                    <span className="text-white">Logout</span>
                </button>
            </div>
        </div>
        </nav>

      <main className="container mx-auto px-4 py-8 flex-grow">
        <EnhancedCarousel items={carouselItems} />

        <section className="mt-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          Security Tools Dashboard
        </h2>
        
        
      </section>
      <EnhancedSecDashboard />
      </main>

      {/* Footer */}
      <Footer />

      {/* User Profile Modal */}
        {isProfileOpen && (
          <UserProfile
            user={currentUser}         
            onClose={() => setIsProfileOpen(false)}
          />
        )}

        {/* Quick Access Modal */}
      {isQuickAccessOpen && (
        <Suspense fallback={<div>Loading...</div>}>
         <QuickAccessModal onClose={() => setIsQuickAccessOpen(false)} />
        </Suspense>
      )}
    </div>
    </>
  );
};

export default Landing;
